// src/features/fourlink-config/components/blocks/Block1Steps/CategoriasProfesionalesStep.jsx

import React, { useMemo, useState } from 'react';
import { CommentButton } from '../../shared/CommentButton';

export const CategoriasProfesionalesStep = ({
  categoriasProfesionalesDisponibles,
  hiddenGruposProfesionales,
  hiddenProfesionalesPorGrupo,
  selectedCategorias,
  collapsedCategoriasProfesionales,
  onToggleCategoria,
  onAddGrupo,
  onAddProfesionalAGrupo,
  onHideGrupos,
  onRestoreGrupo,
  onHideProfesionales,
  onRestoreProfesional,
  onToggleCollapse,
  onCommentClick,
  hasComment,
  getCategoriaComment,
}) => {
  const categoriasSectionKey = 'section:categorias-profesionales';
  const [isAddGrupoModalOpen, setIsAddGrupoModalOpen] = useState(false);
  const [isHideGruposModalOpen, setIsHideGruposModalOpen] = useState(false);
  const [newGrupoName, setNewGrupoName] = useState('');
  const [gruposToHide, setGruposToHide] = useState([]);

  const [grupoModalData, setGrupoModalData] = useState(null); // {grupo, mode:'add'|'hide'}
  const [newProfesionalName, setNewProfesionalName] = useState('');
  const [profesionalesToHide, setProfesionalesToHide] = useState([]);

  const gruposVisibles = useMemo(
    () => Object.keys(categoriasProfesionalesDisponibles || {}),
    [categoriasProfesionalesDisponibles],
  );

  const hiddenGruposList = useMemo(
    () => (Array.isArray(hiddenGruposProfesionales) ? hiddenGruposProfesionales : []),
    [hiddenGruposProfesionales],
  );

  const toggleGrupoToHide = (grupo) => {
    setGruposToHide((prev) =>
      prev.includes(grupo) ? prev.filter((g) => g !== grupo) : [...prev, grupo],
    );
  };

  const handleConfirmHideGrupos = () => {
    if (typeof onHideGrupos === 'function' && gruposToHide.length > 0) {
      onHideGrupos(gruposToHide, categoriasProfesionalesDisponibles);
    }
    setGruposToHide([]);
    setIsHideGruposModalOpen(false);
  };

  const handleCreateGrupo = (e) => {
    e.preventDefault();
    if (!newGrupoName.trim()) return;
    const created = typeof onAddGrupo === 'function' ? onAddGrupo(newGrupoName.trim()) : false;
    if (created === false) return;
    setNewGrupoName('');
    setIsAddGrupoModalOpen(false);
  };

  const openProfesionalesModal = (grupo, mode) => {
    setGrupoModalData({ grupo, mode });
    setNewProfesionalName('');
    setProfesionalesToHide([]);
  };

  const handleCreateProfesional = (e) => {
    e.preventDefault();
    if (!grupoModalData?.grupo || !newProfesionalName.trim()) return;
    const created = typeof onAddProfesionalAGrupo === 'function'
      ? onAddProfesionalAGrupo(grupoModalData.grupo, newProfesionalName.trim())
      : false;
    if (created === false) return;
    setNewProfesionalName('');
    setGrupoModalData(null);
  };

  const handleHideProfesionales = () => {
    if (!grupoModalData?.grupo || profesionalesToHide.length === 0) return;
    if (typeof onHideProfesionales === 'function') {
      onHideProfesionales(grupoModalData.grupo, profesionalesToHide);
    }
    setProfesionalesToHide([]);
    setGrupoModalData(null);
  };

  const toggleProfesionalToHide = (prof) => {
    setProfesionalesToHide((prev) =>
      prev.includes(prof) ? prev.filter((p) => p !== prof) : [...prev, prof],
    );
  };

  const renderModalShell = (children) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 relative">
        {children}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-800">Categorias Profesionales</h2>
        <div className="flex items-center gap-3">
          {hasComment && (
            <span className="text-xs font-semibold text-blue-700">Comentario guardado</span>
          )}
          <button
            type="button"
            onClick={() => setIsHideGruposModalOpen(true)}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-red-200 text-red-700 bg-white hover:bg-red-50 transition-colors font-semibold"
            title="Ocultar grupo"
          >
            <span className="sr-only">Ocultar grupo</span>
            <span className="text-lg leading-none font-bold">-</span>
          </button>
          <button
            type="button"
            onClick={() => setIsAddGrupoModalOpen(true)}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-green-200 text-green-700 bg-white hover:bg-green-50 transition-colors"
            title="Añadir grupo de categoría profesional"
          >
            <span className="text-lg leading-none font-bold">+</span>
            <span className="sr-only">Anadir grupo</span>
          </button>
          <CommentButton
            onClick={() => onCommentClick({ key: categoriasSectionKey, label: 'Categorías Profesionales' })}
            hasComment={hasComment}
            srLabel="Añadir comentario para Categorías Profesionales"
            variant="circle"
            className="border-blue-200 text-blue-700 bg-white hover:bg-blue-50"
          />
        </div>
      </div>
      <p className="text-gray-600 mb-6">Selecciona las categorias profesionales:</p>
      
      <div className="space-y-4">
        {Object.keys(categoriasProfesionalesDisponibles || {}).length === 0 ? (
          <div className="p-4 border rounded-lg bg-gray-50 text-gray-600">
            No hay categorias profesionales disponibles.
          </div>
        ) : (
          Object.entries(categoriasProfesionalesDisponibles).map(([categoria, profesionales]) => {
            const categoriaKey = `categoria-profesional:${categoria}`;
            const categoriaHasComment = getCategoriaComment(categoria);
            const isCollapsed = collapsedCategoriasProfesionales[categoria] ?? true;
            const hiddenList = hiddenProfesionalesPorGrupo?.[categoria] || [];
            
            return (
              <div key={categoria} className="border rounded-lg">
            <div className="flex items-center justify-between gap-3 p-3 bg-gray-100 rounded-t-lg">
              <button
                type="button"
                onClick={() => onToggleCollapse(categoria)}
                className="flex flex-1 items-center justify-between text-left px-3 py-2 border border-blue-300 rounded-full text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <span className="font-semibold text-lg text-blue-700">{categoria}</span>
                <span aria-hidden="true" className="text-sm">
                  {isCollapsed ? '▼' : '▲'}
                </span>
              </button>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openProfesionalesModal(categoria, 'hide')}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-red-200 text-red-700 bg-white hover:bg-red-50 transition-colors"
                      title={`Ocultar categorías de ${categoria}`}
                    >
                      <span className="sr-only">Ocultar categorías</span>
                      <span className="text-lg leading-none font-bold">-</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => openProfesionalesModal(categoria, 'add')}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-green-200 text-green-700 bg-white hover:bg-green-50 transition-colors"
                      title={`Añadir categoría profesional a ${categoria}`}
                    >
                      <span className="text-lg leading-none font-bold">+</span>
                      <span className="sr-only">Anadir categoria</span>
                    </button>
                    <CommentButton
                      onClick={() => onCommentClick({ key: categoriaKey, label: `Categoria profesional ${categoria}` })}
                      hasComment={categoriaHasComment}
                      srLabel={`${categoriaHasComment ? 'Editar' : 'Añadir'} comentario para la categoría profesional ${categoria}`}
                      variant="circle"
                    />
                    {categoriaHasComment && (
                      <span className="text-xs font-semibold text-blue-700">Comentario guardado</span>
                    )}
                  </div>
                </div>
                {!isCollapsed && (
                  <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {profesionales.map((prof) => (
                      <button
                        key={prof}
                        type="button"
                        onClick={() => onToggleCategoria(prof)}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          selectedCategorias.includes(prof)
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 hover:border-blue-300'
                        }`}
                      >
                        {prof}
                      </button>
                    ))}
                  </div>
                )}
                {!isCollapsed && hiddenList.length > 0 && (
                  <div className="p-3 border-t text-sm text-gray-600">
                    <strong>Ocultas en el grupo:</strong> {hiddenList.join(', ')}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {isAddGrupoModalOpen && renderModalShell(
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Añadir grupo</h3>
              <p className="text-sm text-gray-600">Crea nuevos grupos y revisa los que has ocultado.</p>
            </div>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-800"
              onClick={() => { setIsAddGrupoModalOpen(false); setNewGrupoName(''); }}
              aria-label="Cerrar modal de añadir grupo"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleCreateGrupo} className="space-y-3">
            <label className="block text-sm font-medium text-gray-700" htmlFor="nuevo-grupo-input">
              Nombre del grupo
            </label>
            <input
              id="nuevo-grupo-input"
              type="text"
              value={newGrupoName}
              onChange={(e) => setNewGrupoName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ej. Enfermería"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setIsAddGrupoModalOpen(false); setNewGrupoName(''); }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                disabled={!newGrupoName.trim()}
              >
                Añadir grupo
              </button>
            </div>
          </form>

          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Grupos ocultos</h4>
            {hiddenGruposList.length === 0 ? (
              <p className="text-sm text-gray-500">No hay grupos ocultos.</p>
            ) : (
              <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {hiddenGruposList.map((item) => (
                  <li key={item} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-800">{item}</span>
                    {typeof onRestoreGrupo === 'function' && (
                      <button
                        type="button"
                        onClick={() => onRestoreGrupo(item)}
                        className="text-xs px-3 py-1 rounded-full border border-blue-200 text-blue-700 hover:bg-blue-50"
                      >
                        Restaurar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {isHideGruposModalOpen && renderModalShell(
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Ocultar grupos</h3>
              <p className="text-sm text-gray-600">Selecciona los grupos que quieras ocultar temporalmente.</p>
            </div>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-800"
              onClick={() => { setIsHideGruposModalOpen(false); setGruposToHide([]); }}
              aria-label="Cerrar modal de ocultar grupos"
            >
              ✕
            </button>
          </div>

          {gruposVisibles.length === 0 ? (
            <p className="text-sm text-gray-500">No hay grupos disponibles para ocultar.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {gruposVisibles.map((grupo) => (
                <label
                  key={grupo}
                  className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-200"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={gruposToHide.includes(grupo)}
                      onChange={() => toggleGrupoToHide(grupo)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-800">{grupo}</span>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setIsHideGruposModalOpen(false); setGruposToHide([]); }}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmHideGrupos}
              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              disabled={gruposToHide.length === 0}
            >
              Ocultar seleccionados
            </button>
          </div>
        </div>
      )}

      {grupoModalData && renderModalShell(
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {grupoModalData.mode === 'add' ? 'Añadir categoría' : 'Ocultar categorías'}
              </h3>
              <p className="text-sm text-gray-600">Grupo: {grupoModalData.grupo}</p>
            </div>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-800"
              onClick={() => { setGrupoModalData(null); setNewProfesionalName(''); setProfesionalesToHide([]); }}
              aria-label="Cerrar modal de categorías"
            >
              ✕
            </button>
          </div>

          {grupoModalData.mode === 'add' && (
            <form onSubmit={handleCreateProfesional} className="space-y-3">
              <label className="block text-sm font-medium text-gray-700" htmlFor="nuevo-profesional-input">
                Nombre de la categoría profesional
              </label>
              <input
                id="nuevo-profesional-input"
                type="text"
                value={newProfesionalName}
                onChange={(e) => setNewProfesionalName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ej. Médico especialista"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setGrupoModalData(null); setNewProfesionalName(''); }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                  disabled={!newProfesionalName.trim()}
                >
                  Añadir categoría
                </button>
              </div>
            </form>
          )}

          {grupoModalData.mode === 'hide' && (
            <>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {(categoriasProfesionalesDisponibles[grupoModalData.grupo] || []).length === 0 ? (
                  <p className="text-sm text-gray-500">No hay categorías disponibles para ocultar.</p>
                ) : (
                  categoriasProfesionalesDisponibles[grupoModalData.grupo].map((prof) => (
                    <label
                      key={prof}
                      className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-200"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={profesionalesToHide.includes(prof)}
                          onChange={() => toggleProfesionalToHide(prof)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-800">{prof}</span>
                      </div>
                      {selectedCategorias.includes(prof) && (
                        <span className="text-xs text-blue-600 font-medium">Seleccionada</span>
                      )}
                    </label>
                  ))
                )}
              </div>

              <div className="border-t pt-3">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Categorías ocultas en este grupo</h4>
                {hiddenProfesionalesPorGrupo?.[grupoModalData.grupo]?.length ? (
                  <ul className="space-y-2 max-h-32 overflow-y-auto pr-1">
                    {hiddenProfesionalesPorGrupo[grupoModalData.grupo].map((item) => (
                      <li key={item} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-sm text-gray-800">{item}</span>
                        {typeof onRestoreProfesional === 'function' && (
                          <button
                            type="button"
                            onClick={() => onRestoreProfesional(grupoModalData.grupo, item)}
                            className="text-xs px-3 py-1 rounded-full border border-blue-200 text-blue-700 hover:bg-blue-50"
                          >
                            Restaurar
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No hay categorías ocultas en este grupo.</p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setGrupoModalData(null); setProfesionalesToHide([]); }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleHideProfesionales}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                  disabled={profesionalesToHide.length === 0}
                >
                  Ocultar seleccionadas
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
